import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { CsrfTokenStore } from '../auth/csrf-token.store';
import { CUSTOMER_AUTH_URL_PREFIX, CustomerSessionService } from '../auth/customer-session.service';

// Mirror of SAFE_METHODS in backend/src/middlewares/csrf.guard.ts — the CsrfGuard
// only requires the header for mutating, cookie-authenticated requests.
const SAFE_METHODS: readonly string[] = ['GET', 'HEAD', 'OPTIONS'];

/**
 * Single HTTP contract with the backend: every request to `apiUrl` carries credentials
 * (HttpOnly cookies), mutations additionally carry `X-CSRF-Token` when the store holds
 * a token, and TWO statuses are answered with one silent refresh plus a single retry —
 * 401 (dead session) and 403 on a mutation made with a token in the store (a CSRF token
 * that no longer matches the cookie). Requests to other origins pass through untouched.
 *
 * WHY 403 EARNS A RETRY AT ALL. `/refresh` ROTATES the CSRF token, so a tab that slept
 * through a refresh started by another request holds the previous one, and the guard
 * answers 403. That is a rotation accident, not a verdict about the customer — and the
 * cure is exactly the 401 cure: refresh, re-decorate, send once more.
 *
 * The 403 branch is narrowed three times, and all three narrowings are load-bearing:
 * the error must be an `HttpErrorResponse` (`status` on anything else is guesswork),
 * the method must be a MUTATION (`CustomerCsrfGuard` lets safe methods through without
 * looking at the token, so a 403 on a GET can never come from double-submit), and the
 * store must actually hold a token (with none there was nothing to rotate, and the
 * request travelled without the header anyway).
 *
 * SIDE EFFECT ACCEPTED KNOWINGLY: an AUTHORISATION 403 on a mutation — one that has
 * nothing to do with CSRF — also spends one refresh and one retry before the ORIGINAL
 * error reaches the caller. It costs a slot of the backend's `@Throttle(10, 60)` on
 * `/refresh` and, today, nothing else.
 *
 * WHAT „nothing else" RESTS ON, AND WHAT IT DOES NOT. It rests on WHERE the throw
 * stands, never on how many places throw — there is more than one source and the number
 * moves. In the `/my/*` family today:
 *   · `assertEstablishmentCustomer`
 *     (`../backend/src/controllers/my-closed-course-requests.controller.ts:32`), called
 *     at the top of both handlers, before either delegates anything;
 *   · `assertSectionsMatchAccount`
 *     (`../backend/src/services/my-profile/update-my-profile.ts:342,350`) — a MUTATION,
 *     `PATCH /my/profile`, and it landed here on 2026-08-13, AFTER this paragraph first
 *     claimed there was only one source. Safe for the same reason and no other: the
 *     assertion is called at `:311`, ahead of the fresh read and of
 *     `customersService.update()` at `:323`, so the repeated request re-runs a handler
 *     that has written nothing;
 *   · the two subscription refusals (`find-my-subscriptions.ts:76`,
 *     `show-my-subscription.ts:47`) sit on GETs, which this branch never retries.
 *
 * A 403 thrown AFTER a partial write would make this retry write TWICE, and no reader
 * of this comment can be asked to re-derive that ordering by hand. Step 3b of
 * `scripts/verify-s13-p4.mjs` reads the whole `/my/*` family and goes red when a
 * `ForbiddenException` can reach the caller after a write inside the same handler.
 *
 * (`establishmentOnlyGuard` on the front is NOT a source — it bounces a navigation
 * inside the router and never produces an API response at all.)
 *
 * The refresh may also answer "I did not ask, the rate limit is running" — the
 * backend throttles `/refresh` per IP and answers 429, which the session service
 * remembers for the length of the window. Only the verdict `'authenticated'` earns
 * the retry; every other one (including that 429) re-throws the ORIGINAL error to
 * the caller, so nobody sees a rate limit dressed up as their own failure.
 *
 * This stays the ONLY interceptor of the panel (project rule) — session handling
 * belongs here rather than being duplicated in feature services.
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  // Origin boundary: `apiUrl` has no trailing slash, so a bare prefix match would
  // also catch foreign origins extending the string (e.g. localhost:50001).
  const isApiRequest =
    req.url === environment.apiUrl ||
    req.url.startsWith(`${environment.apiUrl}/`);
  if (!isApiRequest) {
    return next(req);
  }

  const csrfTokenStore = inject(CsrfTokenStore);
  const session = inject(CustomerSessionService);

  const withSessionHeaders = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
    const csrfToken = csrfTokenStore.token();
    const isMutation = !SAFE_METHODS.includes(request.method);

    return request.clone({
      withCredentials: true,
      ...(isMutation && csrfToken !== null
        ? { setHeaders: { 'X-CSRF-Token': csrfToken } }
        : {}),
    });
  };

  const firstAttempt = next(withSessionHeaders(req));

  // The auth endpoints ARE the recovery path: retrying them through a refresh
  // would loop (a 401 from /refresh would trigger another /refresh), and a failed
  // login must surface to the caller as-is.
  if (req.url.startsWith(CUSTOMER_AUTH_URL_PREFIX)) {
    return firstAttempt;
  }

  /**
   * The recovery condition, ENUMERATED — never a range and never a negation. Each
   * clause names a case that really means "the session material is stale"; anything
   * not listed here is somebody else's failure and travels to the caller untouched.
   */
  const isRecoverable = (error: unknown): boolean => {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    if (error.status === 401) {
      return true;
    }

    return (
      error.status === 403 &&
      !SAFE_METHODS.includes(req.method) &&
      csrfTokenStore.token() !== null
    );
  };

  return firstAttempt.pipe(
    catchError((error: unknown) => {
      if (!isRecoverable(error)) {
        return throwError(() => error);
      }

      return session.refresh().pipe(
        switchMap((outcome) =>
          // Re-decorate rather than replay the original request: /refresh rotates
          // the CSRF token, so the retry must carry the freshly stored one.
          // Compared against the one named verdict that means "session confirmed",
          // never negated and never as a truthiness test.
          outcome === 'authenticated' ? next(withSessionHeaders(req)) : throwError(() => error),
        ),
      );
    }),
  );
};
